import React, { useContext, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LoginIcon from "@mui/icons-material/Login";
import LinkIcon from "@mui/icons-material/Link";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { UserContext } from "../../components/auth/auth-context";
import { getToken } from "../../util/authUtils";

const DEFAULT_AGENTCRAFTY_API_URL =
  process.env.REACT_APP_AGENTCRAFTY_API_URL || "https://agentcrafty.com/api/backend";

function readParam(search, key) {
  return new URLSearchParams(search).get(key) || "";
}

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function getAgentCraftyApiUrl(search) {
  const fromLink = readParam(search, "agentcraftyApiUrl");
  return String(fromLink || DEFAULT_AGENTCRAFTY_API_URL).replace(/\/+$/, "");
}

function safeHttpUrl(value) {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:" ? url.toString() : "";
  } catch (_error) {
    return "";
  }
}

function getAgentCraftyReturnUrl(search) {
  const fromLink = safeHttpUrl(readParam(search, "agentcraftyReturnUrl"));
  if (fromLink) return fromLink;

  const apiUrl = safeHttpUrl(getAgentCraftyApiUrl(search));
  if (!apiUrl) return "https://agentcrafty.com/agents";

  const url = new URL(apiUrl);
  url.pathname = "/agents";
  url.search = "";
  url.hash = "";
  return url.toString();
}

async function readJsonResponse(response, fallbackMessage) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch (_error) {
    return {
      error: response.ok ? fallbackMessage : text || fallbackMessage,
    };
  }
}

export default function ConnectAgentCraftyPage() {
  const location = useLocation();
  const { providerUser } = useContext(UserContext);
  const token = useMemo(() => readParam(location.search, "token"), [location.search]);
  const subscriberId = useMemo(() => readParam(location.search, "subscriberId"), [location.search]);
  const expectedEmail = useMemo(() => normalizeEmail(readParam(location.search, "email")), [location.search]);
  const agentCraftyApiUrl = useMemo(() => getAgentCraftyApiUrl(location.search), [location.search]);
  const agentCraftyReturnUrl = useMemo(() => getAgentCraftyReturnUrl(location.search), [location.search]);
  const currentPath = `${location.pathname}${location.search}`;
  const authToken = getToken();
  const loggedInEmail = normalizeEmail(providerUser?.email);
  const isLoggedIn = Boolean(authToken && providerUser?.userId && Number(providerUser.userId) > 0);
  const emailMatches = !expectedEmail || !loggedInEmail || expectedEmail === loggedInEmail;
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState("");

  const handleConnect = async () => {
    if (!token || !subscriberId) {
      setError("This AgentCrafty connection link is missing required details.");
      return;
    }

    if (!isLoggedIn) {
      setError("Sign in to Timely365 to complete this connection.");
      return;
    }

    if (!emailMatches) {
      setError(`Sign in as ${expectedEmail} to complete this AgentCrafty connection.`);
      return;
    }

    setConnecting(true);
    setError("");

    try {
      const response = await fetch(`${agentCraftyApiUrl}/v1/care/plany365/confirm`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
        body: JSON.stringify({
          token,
          subscriberId,
          email: loggedInEmail || expectedEmail,
        }),
      });
      const payload = await readJsonResponse(response, "AgentCrafty connection service is unavailable.");
      if (!response.ok) {
        const message = payload.error || "Could not complete the AgentCrafty connection.";
        throw new Error(
          /subscriber not found|could not find this workspace/i.test(message)
            ? "AgentCrafty could not find this workspace. Go back to AgentCrafty, create a fresh Timely365 connection token, then open the new link."
            : message
        );
      }
      setConnected(true);
      if (agentCraftyReturnUrl) {
        window.setTimeout(() => {
          window.location.href = agentCraftyReturnUrl;
        }, 900);
      }
    } catch (err) {
      setError(err.message || "Could not complete the AgentCrafty connection.");
    } finally {
      setConnecting(false);
    }
  };

  return (
    <Box sx={{ minHeight: "calc(100vh - 64px)", bgcolor: "background.default", py: { xs: 4, md: 8 } }}>
      <Container maxWidth="sm">
        <Stack
          spacing={3}
          sx={{
            bgcolor: "background.paper",
            border: 1, borderColor: "divider",
            borderRadius: 2,
            p: { xs: 3, md: 4 },
            boxShadow: "0 12px 30px rgba(34, 55, 74, 0.08)",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <LinkIcon sx={{ color: "text.primary", fontSize: 34 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "text.primary" }}>
                Connect AgentCrafty
              </Typography>
              <Typography sx={{ color: "text.secondary" }}>
                Link this Timely365 account so AgentCrafty can use Timely365 connected tools.
              </Typography>
            </Box>
          </Stack>

          {!token || !subscriberId ? (
            <Alert severity="error">This AgentCrafty connection link is incomplete.</Alert>
          ) : connected ? (
            <Stack spacing={2}>
              <Alert icon={<CheckCircleOutlineIcon />} severity="success">
                AgentCrafty is connected to Timely365.
              </Alert>
              <Button
                component="a"
                href={agentCraftyReturnUrl}
                variant="contained"
                endIcon={<OpenInNewIcon />}
                sx={{ bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" } }}
              >
                Back to AgentCrafty
              </Button>
              <Button component={RouterLink} to="/biz/dashboard?v=care" variant="outlined">
                Open Timely365
              </Button>
            </Stack>
          ) : (
            <>
              <Alert severity={isLoggedIn ? "info" : "warning"}>
                {isLoggedIn
                  ? "You are signed in to Timely365. Confirm the connection below."
                  : "Sign in to Timely365 first. After login, you will return here to finish the connection."}
              </Alert>

              <TextField
                label="Workspace owner email"
                value={expectedEmail || loggedInEmail || ""}
                InputProps={{ readOnly: true }}
                disabled
                fullWidth
              />

              {isLoggedIn && expectedEmail && loggedInEmail && !emailMatches ? (
                <Alert severity="error">
                  This link is for {expectedEmail}, but you are signed in as {loggedInEmail}.
                </Alert>
              ) : null}

              {error ? <Alert severity="error">{error}</Alert> : null}

              {isLoggedIn ? (
                <Button
                  variant="contained"
                  size="large"
                  startIcon={connecting ? <CircularProgress color="inherit" size={18} /> : <LinkIcon />}
                  onClick={handleConnect}
                  disabled={connecting || !emailMatches}
                  sx={{ bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" } }}
                >
                  {connecting ? "Connecting..." : "Connect AgentCrafty"}
                </Button>
              ) : (
                <Button
                  component={RouterLink}
                  to={`/login?email=${encodeURIComponent(expectedEmail)}&returnTo=${encodeURIComponent(currentPath)}`}
                  variant="contained"
                  size="large"
                  startIcon={<LoginIcon />}
                  sx={{ bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" } }}
                >
                  Sign in to Timely365
                </Button>
              )}
            </>
          )}
        </Stack>
      </Container>
    </Box>
  );
}
